import { Icon } from "armstrong-react";
import classNames from "classnames";
import React from "react";
import * as ReactDOM from "react-dom";
import Slider from "react-slick";
import { Settings as SliderSettings } from "react-slick";
import _ from "underscore";
import { CircleButton, FlowBox, PreventLinkDragging } from "../../../core/atoms/atoms";
import { isServer } from "../../../core/detection";
import { getWindowClientRect, intersects, supportsIntersectionObserver } from "../../../../../packages/ui/src/utils";

import "./swipeCarousel.scss";

interface Props {
  slickSettings?: SliderSettings;

  /**
   * Label to use for the "next" button. Pass arrows: false to slickSettings to hide the default buttons.
   */
  nextLabel?: string;

  /**
   * Label to use for the "next" button. Pass arrows: false to slickSettings to hide the default buttons.
   */
  previousLabel?: string;
}

interface State {
  currentSlideIndex: number;
  visibleSlideCount: number;
  entered: boolean;
}

/*
 * A swipeable carousel generator that can accept any number of components as children, on touch enabled screens
 * elements will be swipeable as you would expect, on non-touch enable screens, the carousel is replace with a carousel
 * that makes use of arrows for scrolling to avoid inconcistencies with how click events are handled.
 */
export class SwipeCarousel extends React.Component<Props, State> {
  private rootRef = React.createRef<HTMLDivElement>();
  private sliderRef = React.createRef<Slider>();

  public static defaultProps: Props = {
    nextLabel: "More",
    previousLabel: "Back",
  };

  public constructor(props: Props) {
    super(props);

    this.state = {
      currentSlideIndex: 0,
      visibleSlideCount: 1,
      entered: false,
    } as State;
  }

  /**
   * Get the number of slides currently on-screen.
   */
  private getVisibleSlideCount = (): number => {
    const { slickSettings } = this.props;
    if (slickSettings && slickSettings.slidesToShow != null) {
      return slickSettings.slidesToShow;
    }

    const slider = ReactDOM.findDOMNode(this.sliderRef.current) as HTMLElement;
    if (!slider) {
      return 1;
    }

    const containerRect = slider.getBoundingClientRect();

    // Iterate left to right to find the first slide that's not within bounds
    let slideCount: number;
    const slickSlides = slider.getElementsByClassName("slick-slide");
    for (let i = 0; i < slickSlides.length; i++) {
      const slide = slickSlides[i] as HTMLElement;
      const slideRect = slide.getBoundingClientRect();

      if (slideRect.right > containerRect.right) {
        // Current slide is out of bounds, so the previous slide is the last "visible" slide
        // N.B. i is 0-indexed whereas slideCount is 1-indexed
        slideCount = i - this.state.currentSlideIndex;
        break;
      }
    }

    return slideCount || slickSlides.length; // if undefined, all slides are visible
  }

  private updateSlideCount = _.debounce(() => this.setState({ visibleSlideCount: this.getVisibleSlideCount() }), 400, false);

  /**
   * Move forward one page, aligned to the end of the list
   */
  private onNextClicked = () => {
    const { currentSlideIndex, visibleSlideCount } = this.state;
    const maxIndex = React.Children.count(this.props.children) - visibleSlideCount;
    const newIndex = Math.min(currentSlideIndex + visibleSlideCount, maxIndex);

    this.sliderRef.current.slickGoTo(newIndex);
  }

  /**
   * Move backward one page, aligned to the start of the list
   */
  private onPreviousClicked = () => {
    const { currentSlideIndex, visibleSlideCount } = this.state;
    const newIndex = Math.max(0, currentSlideIndex - visibleSlideCount);
    this.sliderRef.current.slickGoTo(newIndex);
  }

  public componentDidMount() {
    this.updateSlideCount();
    window.addEventListener("resize", this.updateSlideCount);

    // Trigger entrance CSS animation by adding a class
    if (!this.state.entered && this.rootRef.current) {
      const currentRect = this.rootRef.current.getBoundingClientRect();
      // Skip entrance animation if in-view, or if no observer support (iPhone 5, IE11)
      if (!supportsIntersectionObserver() || intersects(currentRect, getWindowClientRect())) {
        this.setState({ entered: true });
      }
      else {
        // If not in view, trigger the animation when in view
        const observer = new IntersectionObserver((entries, obs) => {
          const [slider] = entries;
          if (slider.isIntersecting) {
            this.setState({ entered: true });
            obs.disconnect();
          }
        }, { threshold: 0 });

        observer.observe(this.rootRef.current);
      }
    }
  }

  public componentWillUnmount() {
    window.removeEventListener("resize", this.updateSlideCount);
  }

  public render() {
    const { children, slickSettings = {}, nextLabel, previousLabel } = this.props;
    const { currentSlideIndex, entered, visibleSlideCount } = this.state;

    // http://kenwheeler.github.io/slick/
    const settings: SliderSettings = {
      slidesToShow: visibleSlideCount,
      variableWidth: true,
      infinite: false,
      swipeToSlide: true,
      edgeFriction: 0.3, // Increase visibility of overscroll at edge
      touchThreshold: 100, // Increase sensitivity when touch swiping
      arrows: false, // See comment on showDefaultArrows
      beforeChange: (currentIndex, newIndex) => this.setState({ currentSlideIndex: newIndex }),
      ...slickSettings,
    };

    // This component shows arrows by default but doesn't use Slick's nextArrow and previousArrow props
    // - those are placed above and below which is annoying. Set slickSettings.arrows to false to override this.
    const showDefaultArrows = slickSettings.arrows == null;

    const slides = React.Children.map(children, component => component && (
      <div className="component-wrapper">
        {component}
      </div>
    ));

    const rootClassName = classNames(
      "swipe-carousel", {
      "swipe-carousel--entered": entered,
    });

    const showMoreDisabled = !slides
      || slides.length <= settings.slidesToShow // At first page and not enough content
      || currentSlideIndex >= (slides.length - settings.slidesToShow); // At last page

    return (
      <div ref={this.rootRef} className={rootClassName}>
        <PreventLinkDragging>
          <Slider ref={this.sliderRef} {...settings}>
            {slides}
          </Slider>
        </PreventLinkDragging>

        {showDefaultArrows && (
          <FlowBox className="swipe-carousel__buttons">
            <CircleButton
              label={previousLabel}
              direction="left"
              icomoon={Icon.Icomoon.arrowLeft3}
              onClick={this.onPreviousClicked}
              disabled={!currentSlideIndex}
            />
            <CircleButton
              label={nextLabel}
              direction="right"
              icomoon={Icon.Icomoon.arrowRight3}
              onClick={this.onNextClicked}
              disabled={showMoreDisabled}
            />
          </FlowBox>
        )}
      </div>
    );
  }
}
